import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCompanyId } from '@/features/auth/hooks/useAuth'
import { estimatesApi } from '@/features/estimates/api/estimates.api'
import { autoLinkService } from '@/services/project/autoLinkService'
import { useToast } from '@/shared/hooks/useToast'
import { translateError } from '@/shared/lib/errorMessages'
import { supabase } from '@/shared/lib/supabase'
import type { Estimate } from '@/entities/estimate/model'

export const estimateKeys = { all: ['estimates'] as const, list: (companyId: string) => [...estimateKeys.all, 'list', companyId] as const }

export function useEstimates() {
  const companyId   = useCompanyId()
  const queryClient = useQueryClient()

  // Realtime: gdy klient zatwierdzi dokument → trigger DB aktualizuje status
  // w cost_estimates → ta subskrypcja odświeża listę natychmiast (~1-2s)
  useEffect(() => {
    if (!companyId || !supabase) return
    const channel = supabase
      .channel(`estimates-status:${companyId}`)
      .on(
        'postgres_changes',
        {
          event:  'UPDATE',
          schema: 'public',
          table:  'cost_estimates',
          filter: `company_id=eq.${companyId}`,
        },
        () => {
          void queryClient.invalidateQueries({ queryKey: estimateKeys.all })
        },
      )
      .subscribe()
    return () => { void supabase?.removeChannel(channel) }
  }, [companyId, queryClient])

  return useQuery({ queryKey: estimateKeys.list(companyId), queryFn: () => estimatesApi.list(companyId) })
}

/** Fetches all estimates for a project WITH their items. Used by ProjectDocuments
 *  for approval hash and approval modal. Do NOT use for list views (use useEstimates). */
export function useProjectEstimatesWithItems(projectId: string | undefined) {
  const companyId = useCompanyId()
  return useQuery({
    queryKey: ['estimates', 'project-with-items', companyId, projectId],
    queryFn: () => estimatesApi.listWithItemsByProject(companyId, projectId as string),
    enabled: !!projectId && !!companyId,
    staleTime: 60_000,
  })
}

export function useEstimateDetail(id: string | undefined, enabled = true) {
  const companyId = useCompanyId()
  return useQuery({
    queryKey: ['estimates', 'detail', companyId, id],
    queryFn: () => estimatesApi.get(id as string, companyId),
    enabled: !!id && !!companyId && enabled,
    staleTime: 30_000,
  })
}
export function useCreateEstimate() {
  const companyId   = useCompanyId()
  const queryClient = useQueryClient()
  const toast       = useToast()
  return useMutation({
    mutationFn: estimatesApi.create,
    async onMutate(variables) {
      const key = estimateKeys.list(companyId)
      await queryClient.cancelQueries({ queryKey: key })
      const tempId = crypto.randomUUID()
      const optimistic = {
        id: tempId, company_id: companyId,
        client_id: variables.client_id ?? null,
        project_id: variables.project_id ?? null,
        number: '…', name: variables.name,
        status: variables.status,
        estimate_type: variables.estimate_type ?? 'preliminary',
        total_net: 0, total_gross: 0,
        notes: variables.notes ?? '',
        valid_until: variables.valid_until ?? null,
        items: variables.items ?? [],
        created_at: new Date().toISOString(),
        _optimistic: true,
      } as unknown as Estimate
      queryClient.setQueryData<Estimate[]>(key, (old = []) => [optimistic, ...old])
      return { tempId }
    },
    onSuccess(data, _vars, context) {
      const key = estimateKeys.list(companyId)
      // P0-1 fix: replace optimistic placeholder with real server data using data.id
      // (not context.tempId). Using tempId was causing stale-ID bugs: edit/delete
      // would target tempId which doesn't exist in DB → 404.
      queryClient.setQueryData<Estimate[]>(key, (old = []) =>
        old.map(e => e.id === context?.tempId
          ? { ...data, _optimistic: false }
          : e)
      )
      // P0-2 fix: invalidate estimates list so the real number replaces '…' placeholder
      // and server state is canonical. Without this the stale optimistic row persists
      // until next navigation/window-focus refetch.
      void queryClient.invalidateQueries({ queryKey: key })
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress', companyId] })
      toast.success('Kosztorys utworzony')
      autoLinkService.link({
        type: 'estimate', id: data.id, companyId,
        clientId: data.client_id, projectId: data.project_id ?? null,
      }).then(() => {
        if (data.project_id) queryClient.invalidateQueries({ queryKey: ['projects', companyId] })
      }).catch((err) => console.warn('[autoLink] estimate link failed:', err))
    },
    onError(error: any, _vars, context) {
      if (context?.tempId) {
        queryClient.setQueryData<Estimate[]>(estimateKeys.list(companyId), (old = []) =>
          old.filter(e => e.id !== context.tempId)
        )
      }
      const msg = error?.message ?? error?.details ?? 'Sprawdź połączenie i spróbuj ponownie'
      toast.error('Nie udało się utworzyć kosztorysu', msg)
      console.error('[estimates] create error:', error)
    },
  })
}
export function useUpdateEstimate() { const companyId = useCompanyId(); const queryClient = useQueryClient(); const toast = useToast(); return useMutation({ mutationFn: ({ id, input }: { id: string; input: Partial<Estimate> }) => estimatesApi.update(id, input, companyId), onSuccess: (data) => { const updated = data as Estimate; queryClient.setQueryData<Estimate[]>(estimateKeys.list(companyId), (old = []) => old.map(e => e.id === updated.id ? updated : e)); queryClient.invalidateQueries({ queryKey: estimateKeys.all }); /* P0-5 fix: also refresh the detail cache so re-opening the edit modal shows the latest data, not stale 30s-old detail that wipes recent changes. */ queryClient.setQueryData(['estimates', 'detail', companyId, updated.id], updated); queryClient.invalidateQueries({ queryKey: ['estimates', 'detail', companyId, updated.id] }); toast.success('Kosztorys zaktualizowany'); if (updated.project_id) { autoLinkService.link({ type: 'estimate', id: updated.id, companyId, clientId: updated.client_id, projectId: updated.project_id }).then(() => { queryClient.invalidateQueries({ queryKey: ['projects', companyId] }) }).catch((err) => console.warn('[autoLink] estimate update link failed:', err)) } }, onError: (error: any) => { const msg = error?.message ?? error?.details ?? 'Sprawdź połączenie i spróbuj ponownie'; toast.error('Błąd zapisu wyceny', msg); console.error('[estimates] update error:', error) } }) }
export function useDeleteEstimate() { const companyId = useCompanyId(); const queryClient = useQueryClient(); const toast = useToast(); return useMutation({ mutationFn: (id: string) => estimatesApi.delete(id, companyId), onMutate: (id) => { queryClient.setQueryData<Estimate[]>(estimateKeys.list(companyId), (old = []) => old.filter(e => e.id !== id)); return { id } }, onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['project_documents'] }); toast.info('Kosztorys usunięty') }, onError: (_error, _id, context) => { if (context?.id) queryClient.invalidateQueries({ queryKey: estimateKeys.all }); toast.error('Nie udało się usunąć kosztorysu') } }) }
